import { useAppSelector } from "@/store/hooks";
import { CommonService } from "@/services";
import { Misc } from "@/constants";
import { User, Mail, Calendar, Shield, Database } from "lucide-react";

interface UserDetailItem {
    label: string;
    value: string | undefined;
    icon: React.ReactNode;
    type?: 'text' | 'email' | 'date';
}

const AccountSettings = () => {
    const authUser = useAppSelector((state) => state.auth?.user);
    const displayUser = authUser || (CommonService._localStorage.getItem(Misc.LOCAL_STORAGE_LOGGED_IN_USER_DATA) 
        ? JSON.parse(CommonService._localStorage.getItem(Misc.LOCAL_STORAGE_LOGGED_IN_USER_DATA)!) 
        : null);

    // Format user details for display
    const userDetails: UserDetailItem[] = [
        {
            label: "Full Name",
            value: displayUser?.name || "Not specified",
            icon: <User className="w-4 h-4" />,
            type: 'text'
        },
        {
            label: "Username",
            value: displayUser?.username || "Not specified",
            icon: <User className="w-4 h-4" />,
            type: 'text'
        },
        {
            label: "Email Address",
            value: displayUser?.email || "Not available",
            icon: <Mail className="w-4 h-4" />,
            type: 'email'
        },
        {
            label: "User ID",
            value: displayUser?._id ? String(displayUser._id) : "Not available",
            icon: <Database className="w-4 h-4" />,
            type: 'text'
        },
        {
            label: "Role",
            value: displayUser?.role || "Standard",
            icon: <Shield className="w-4 h-4" />,
            type: 'text'
        },
        {
            label: "Status",
            value: displayUser?.status || "Unknown",
            icon: <Shield className="w-4 h-4" />,
            type: 'text'
        },
        {
            label: "Website",
            value: displayUser?.website || "Not specified",
            icon: <Database className="w-4 h-4" />,
            type: 'text'
        },
        {
            label: "Onboarded Date",
            value: displayUser?.onBoardedDate ? new Date(displayUser.onBoardedDate).toLocaleDateString() : "Not available",
            icon: <Calendar className="w-4 h-4" />,
            type: 'date'
        }
    ];

    const formatValue = (item: UserDetailItem) => {
        if (!item.value || item.value === "Not specified" || item.value === "Not available") {
            return <span className="text-[var(--muted-foreground)]">{item.value}</span>;
        }

        if (item.type === 'email' && item.value !== "Not available") {
            return (
                <a 
                    href={`mailto:${item.value}`}
                    className="text-[var(--primary)] hover:underline"
                >
                    {item.value}
                </a>
            );
        }

        return <span className="text-[var(--foreground)]">{item.value}</span>;
    };

    return (
        <div className="settings-content">
            <div className="space-y-6">
                <div>
                    <h3 className="text-lg font-semibold text-[var(--foreground)] mb-2">Account Overview</h3>
                    <p className="text-sm text-[var(--muted-foreground)]">
                        View your account details and information
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {userDetails.map((item, index) => (
                        <div key={index} className="p-4 rounded-lg border border-[var(--border)] bg-[var(--card)]">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="text-[var(--muted-foreground)]">
                                    {item.icon}
                                </div>
                                <h4 className="text-sm font-medium text-[var(--foreground)]">
                                    {item.label}
                                </h4>
                            </div>
                            <div className="text-sm">
                                {formatValue(item)}
                            </div>
                        </div>
                    ))}
                </div>

            </div>
        </div>
    );
};

export default AccountSettings;

